export enum PropertyStatus {
  Active = 'نشط',
  UnderMaintenance = 'تحت الصيانة',
  Sold = 'مباع'
}

export enum UnitType {
  Apartment = 'شقة',
  Villa = 'فيلا',
  Studio = 'استوديو',
  Store = 'محل تجاري',
  Basement = 'سرداب',
  Floor = 'دور كامل'
}

export interface Unit {
  id: string;
  unitNumber: string;
  type: string; // Changed to string to support Arabic values directly from DB
  rent: number;
  contractRent: number;
  rooms: number;
  bathrooms: number;
  area: number;
  tenantId?: string | null;
  tenantName?: string | null; // For display
  status?: 'vacant' | 'occupied'; // For display
}

export interface Property {
  id: string;
  name: string;
  ownerName?: string;
  image?: string;
  governorate: string;
  city: string;
  block: string;
  street: string;
  buildingNumber: string;
  civilId: string;
  type: string;
  buildYear: string;
  area: number;
  status: string;
  bankAccount: string;
  bookValue: number;
  estimatedValue: number;
  units: Unit[];
  createdAt?: string;
  // Calculated frontend stats
  occupancyRate?: number;
  collectedAmount?: number;
  totalExpectedAmount?: number;
  occupiedUnitsCount?: number;
  totalUnitsCount?: number;
}

export enum TenantType {
  Individual = 'Individual',
  Company = 'Company'
}

export interface Tenant {
  id: string;
  type: TenantType;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  language: string;
  civilId: string;
  nationality: string;
  contractStart: string;
  contractEnd: string;
  predictedLeaving: string;
  hasLegalCase: boolean;
  sendWelcomeEmail: boolean;
  sendWelcomeSMS: boolean;
  rentPaymentDay: number;
  paymentCycle: string;
  // Units are now linked via the Unit object, but for the form we need to know which units to assign
  assignedUnits?: { propertyId: string; unitId: string; rent: number }[]; 
  warning?: string; // For backend messages
  unit_number?: string; // From join
  property_name?: string; // From join
}

export interface Invoice {
  id: string;
  date: string;
  dueDate: string; // Due date
  monthDue: string;
  tenantId: string;
  tenantName: string;
  propertyId: string;
  propertyName: string;
  ownerName?: string;
  unitId?: string; // Made optional
  unitNumber: string;
  amount: number;
  serviceCharge?: number;
  contractRent: number;
  isPaid: boolean;
  status: 'Pending' | 'Paid' | 'Late';
  paymentId?: string;
  paymentMethod?: string;
  paymentReference?: string;
  paymentDate?: string;
  collectedBy?: number;
  tenantCivilId?: string;
  contractStart?: string;
  contractEnd?: string;
}

export interface Transaction {
  id: string;
  categoryId: string;
  categoryName?: string;
  amount: number;
  date: string;
  description?: string;
  type?: 'Expense' | 'Income';
  propertyId: string;
  propertyName: string;
  unitId?: string | number | null;
  tenantId?: string;
  tenantName?: string;
  invoiceId?: string; // Linked invoice
}

export interface MaintenanceRequest {
  id: string;
  propertyId: string;
  propertyName: string;
  unitId?: string;
  unitNumber?: string;
  companyId?: string;
  companyName?: string;
  description: string;
  status: 'Pending' | 'In Progress' | 'Completed';
  priority: 'Low' | 'Medium' | 'High';
  dateReported: string;
  cost?: number; // Legacy
  finalCost?: number;
  completionDate?: string;
  completionNotes?: string;
}

export interface MaintenanceCompany {
  id: string;
  name: string;
  specialty: string;
  contactName: string;
  phone: string;
  email: string;
  status: 'Active' | 'Inactive';
  createdAt?: string;
}

export interface MaintenanceContract {
  id: string;
  companyId: string;
  companyName?: string;
  propertyId?: string; // Null = Global
  propertyName?: string;
  startDate: string; // ISO Date
  endDate: string; // ISO Date
  contractValue: number;
  paymentTerms: string;
  description: string;
  status: 'Active' | 'Expired' | 'Cancelled';
}

export interface Employee {
  id: string;
  fullName: string;
  civilId: string;
  position: string;
  baseSalary: number;
  status: string;
  salaryDate?: number;
  createdAt: string;
}

export interface SalaryPayment {
  id: string;
  employeeId: string;
  employeeName: string;
  civilId?: string;
  amount: number;
  paymentDate: string;
  period: string;
  status: string;
}

export interface UserPermissions {
  dashboard: boolean;
  properties: boolean;
  tenants: boolean;
  invoices: boolean;
  expenses: boolean;
  maintenance: boolean;
  employees: boolean;
  reports: boolean;
  settings: boolean;
  userManagement: boolean;
}

export interface AppUser {
  id: string;
  username: string;
  fullName: string;
  password?: string;
  status: 'Active' | 'Disabled';
  permissions: UserPermissions;
}

export interface TransactionCategory {
  id: string;
  name: string;
  type: 'Expense' | 'Income';
}

export interface CompanyInfo {
  id?: number;
  name_ar: string;
  name_en: string;
  address_ar: string;
  address_en: string;
  phone: string;
  fax: string;
  email: string;
  cr_number: string;
  logo_url?: string;
  updated_at?: string;
  auto_invoice_day?: number;
}

export interface ContractAttachment {
  id: string;
  entity_type: 'tenant' | 'maintenance_contract';
  entity_id: string;
  file_name: string;
  file_type: string;
  uploaded_at: string;
}